import { useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import Button from "../../common/Button/Button";

const DomesticHelperBottomStickyBtnLayout = ({
  children,
  btn_label,
  btn_onClick,
  btn_disabled,
  show_summary = false,
  price,
  AdditionalComponent,
  hideContinueBtn = false,
  addonComponent,
  isLoading = false,
  btn_text,
}) => {
  const { t } = useTranslation();
  const translate = useLanguageTranslate();

  // Resolve the stable portal target rendered by NewDomesticHelperPolicy.
  // Falls back to inline rendering when used outside that flow.
  //
  // Before paint, not after: on a plain useEffect the first frame of every step
  // renders this footer inline, inside the page wrapper framer-motion is
  // transforming. A transformed ancestor becomes the containing block for a
  // position:fixed child, so that frame puts the footer in the wrong place at
  // the wrapper's starting opacity, and the next frame snaps it into the
  // portal. useLayoutEffect places it before the browser paints, so the
  // intermediate frame is never shown.
  const [portalTarget, setPortalTarget] = useState(null);
  useLayoutEffect(() => {
    if (typeof document === "undefined") return;
    setPortalTarget(document.getElementById("dh-footer-portal"));
  }, []);

  const footer = (
      <div className="footer items-center bg-white border-t border-[#E5E9EB] w-full py-2 flex justify-end fixed pr-10 bottom-0 max767:w-full max767:px-4 max767:pt-3 max767:pb-[max(2.25rem,calc(env(safe-area-inset-bottom)+1rem))] max767:inset-x-0 ipad:items-center max767:inline-block max767:z-[9] left-0">
        {addonComponent && addonComponent}
        {show_summary && price > 0 && (
          <div className="view_summary mr-[2%] max767:w-full max767:flex max767:mb-2 max767:justify-between ipad:mt-0 rtl:ml-3 rtl:mr-0">
            <span className="text-[#00384D] text-center text-base not-italic font-semibold leading-[26px] tracking-[-0.16px] underline mr-3 rtl:ml-3 rtl:mr-0">
              {t("motorInsurance.bottomStickybtnlayout.summary")}
            </span>
            <strong className="text-[#1A2024] text-xl not-italic font-extrabold leading-6">
              {translate("BHD", "د.ب")} {price}
            </strong>
          </div>
        )}
        <div className="flex justify-end pr-0 rtl:pl-[3%] max767:w-full max767:pr-[0%]">
          <div className="flex max767:w-full max767:flex-col max767:gap-2">
            {AdditionalComponent && AdditionalComponent}
            {/* `type="submit"` carries no weight once this footer is portaled:
                the button lands outside the caller's <form> in the DOM, and
                form association follows DOM ancestry, so no submit event is
                ever fired. Callers wrapping their fields in a form must still
                pass btn_onClick — the form's own onSubmit will only catch
                Enter pressed inside a field. */}
            {!hideContinueBtn && (
              <Button
                isLoading={isLoading}
                disabled={btn_disabled}
                onSubmit={btn_onClick ? btn_onClick : () => {}}
                type="submit"
                label={
                  btn_label ||
                  btn_text ||
                  t(
                    "motorInsurance.newMotorInsuranceFlow.basicInfo.continueButton"
                  )
                }
                className="mb-0 stickybutton flex w-[200px] flex-col items-center gap-2.5 shrink-0 p-4 bg-[#55C6EF] rounded-[10px] text-[#48535B] text-center text-lg max767:text-sm not-italic font-semibold leading-6 tracking-[-0.18px] max767:w-full max767:h-[48px] max767:py-3"
              />
            )}
          </div>
        </div>
      </div>
  );

  return (
    <>
      {children}
      {portalTarget ? createPortal(footer, portalTarget) : footer}
    </>
  );
};

export default DomesticHelperBottomStickyBtnLayout;
